import { useState, useCallback, useEffect, useRef } from 'react';
import { loadGoogleMaps } from '../utils/googleMapsLoader';

export interface PlaceResult {
  placeId: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  types: string[];
}

interface UsePlacesAutocompleteOptions {
  debounceMs?: number;
  types?: string[];
  componentRestrictions?: { country: string | string[] };
}

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

// Delegate to the single shared loader instead of injecting a second script.
// This file used to append its own places-only script tag (no existing-script
// check at all), which loaded the Maps API a second time into a separate realm
// and broke AdvancedMarkerElement elsewhere with "not an instance of
// MapsApiMap". One loader app-wide = one realm.
function loadGoogleMapsScript(): Promise<void> {
  return loadGoogleMaps();
}

export function usePlacesAutocomplete(options: UsePlacesAutocompleteOptions = {}) {
  const {
    debounceMs = 300,
    types = ['establishment'],
    componentRestrictions = { country: 'sa' }, // Saudi Arabia
  } = options;

  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<PlaceResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<PlaceResult | null>(null);

  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize Google Places service
  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) {
      setError('Google Maps API key not configured');
      return;
    }

    let isMounted = true;

    async function init() {
      try {
        await loadGoogleMapsScript();

        if (!isMounted) return;

        autocompleteServiceRef.current = new google.maps.places.AutocompleteService();

        // Create a hidden div for PlacesService (required)
        const placesDiv = document.createElement('div');
        placesDiv.style.display = 'none';
        document.body.appendChild(placesDiv);

        // Create a temporary map for PlacesService
        const tempMap = new google.maps.Map(placesDiv, {
          center: { lat: 24.7136, lng: 46.6753 }, // Riyadh
          zoom: 10,
        });

        placesServiceRef.current = new google.maps.places.PlacesService(tempMap);
        setIsReady(true);
        setError(null);
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to initialize Places service');
        }
      }
    }

    init();

    return () => {
      isMounted = false;
    };
  }, []);

  // Search for places
  const search = useCallback(
    async (searchQuery: string) => {
      if (!searchQuery.trim() || !isReady || !autocompleteServiceRef.current) {
        setSuggestions([]);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const request: google.maps.places.AutocompletionRequest = {
          input: searchQuery,
          types,
          componentRestrictions,
        };

        autocompleteServiceRef.current.getPlacePredictions(
          request,
          (predictions, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
              const results: PlaceResult[] = predictions.map((prediction) => ({
                placeId: prediction.place_id,
                name: prediction.structured_formatting.main_text,
                address: prediction.structured_formatting.secondary_text || '',
                lat: 0,
                lng: 0,
                types: prediction.types || [],
              }));
              setSuggestions(results);
            } else if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
              setSuggestions([]);
            } else {
              console.error('Places autocomplete error:', status);
              setSuggestions([]);
            }
            setIsLoading(false);
          }
        );
      } catch (err) {
        console.error('Places search error:', err);
        setError(err instanceof Error ? err.message : 'Search failed');
        setSuggestions([]);
        setIsLoading(false);
      }
    },
    [isReady, types, componentRestrictions]
  );

  // Debounced search
  const debouncedSearch = useCallback(
    (searchQuery: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        search(searchQuery);
      }, debounceMs);
    },
    [search, debounceMs]
  );

  // Handle query change
  const handleQueryChange = useCallback(
    (newQuery: string) => {
      setQuery(newQuery);
      if (newQuery.trim()) {
        debouncedSearch(newQuery);
      } else {
        setSuggestions([]);
      }
    },
    [debouncedSearch]
  );

  // Get place details (coordinates)
  const getPlaceDetails = useCallback(
    (placeId: string): Promise<PlaceResult | null> => {
      return new Promise((resolve) => {
        if (!placesServiceRef.current) {
          resolve(null);
          return;
        }

        const request: google.maps.places.PlaceDetailsRequest = {
          placeId,
          fields: ['place_id', 'name', 'formatted_address', 'geometry', 'types'],
        };

        placesServiceRef.current.getDetails(request, (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place) {
            const result: PlaceResult = {
              placeId: place.place_id || placeId,
              name: place.name || '',
              address: place.formatted_address || '',
              lat: place.geometry?.location?.lat() || 0,
              lng: place.geometry?.location?.lng() || 0,
              types: place.types || [],
            };
            resolve(result);
          } else {
            console.error('Place details error:', status);
            resolve(null);
          }
        });
      });
    },
    []
  );

  // Select a place from suggestions
  const selectPlace = useCallback(
    async (place: PlaceResult) => {
      setIsLoading(true);
      const details = await getPlaceDetails(place.placeId);
      if (details) {
        setSelectedPlace(details);
        setQuery(details.name);
        setSuggestions([]);
      }
      setIsLoading(false);
    },
    [getPlaceDetails]
  );

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedPlace(null);
    setQuery('');
    setSuggestions([]);
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    query,
    setQuery: handleQueryChange,
    suggestions,
    isLoading,
    isReady,
    error,
    selectedPlace,
    selectPlace,
    clearSelection,
    getPlaceDetails,
  };
}

export default usePlacesAutocomplete;
